/**
 * @author      hugo@zumper.com (Hugo Pineda)
 * @copyright   Copyright (c) 2022, Zumper
 */

// https://chakra-ui.com/docs/styled-system/theming/component-style#styling-single-part-components

import type { ComponentStyleConfig } from '@chakra-ui/theme'
import { SystemStyleFunction, SystemStyleObject, mode } from '@chakra-ui/theme-tools'

import { focus } from '../sharedStyles'

const baseStyle: SystemStyleObject = {
  bg: 'transparent',
  borderRadius: 6,
  borderWidth: 0,
  cursor: 'pointer',
  fontWeight: 'extrabold',
  fontFamily: 'body',
  lineHeight: 0.9,
  _focus: {
    outline: 'none',
    boxShadow: 'none',
  },
  _focusVisible: focus,
  _disabled: {
    opacity: 0.4,
  },
}

const sizes: SystemStyleObject = {
  xs: {
    fontSize: 'sm',
    px: 3,
    height: 8,
  },
  sm: {
    fontSize: 'md',
    px: 4,
    height: 9,
  },
  md: {
    fontSize: 'md',
    px: 4,
    height: 10,
  },
  lg: {
    fontSize: 'md',
    px: 6,
    height: 12,
  },
}

const variantSolid: SystemStyleFunction = (props) => ({
  color: 'white',
  bg: mode('blue.900', 'pink.600')(props),
  _hover: {
    bg: mode('blue.800', 'pink.650')(props),
    _disabled: {
      bg: mode('blue.900', 'pink.600')(props),
    },
  },
  _active: {
    bg: mode('blue.800', 'pink.650')(props),
    _disabled: {
      bg: mode('blue.900', 'pink.600')(props),
    },
  },
})

const variantPink: SystemStyleFunction = (props) => ({
  color: 'white',
  bg: 'pink.600',
  _hover: {
    bg: 'pink.650',
    _disabled: {
      bg: 'pink.600',
    },
  },
})

const variantSecondary: SystemStyleFunction = (props) => ({
  color: mode('gray.950', 'white')(props),
  bg: mode('gray.150', 'gray.950')(props),
  _hover: {
    bg: mode('gray.250', 'gray.800')(props),
    _disabled: {
      bg: mode('gray.150', 'gray.950')(props),
    },
  },
})

const variantGhost: SystemStyleFunction = (props) => ({
  color: mode('gray.950', 'white')(props),
  bg: 'transparent',
  _hover: {
    bg: mode('gray.50', 'gray.800')(props),
    _disabled: {
      bg: 'transparent',
    },
  },
})

const variantOutline: SystemStyleFunction = (props) => ({
  color: mode('gray.950', 'white')(props),
  bg: 'white',
  borderColor: mode('gray.350', 'gray.950')(props),
  _hover: {
    bg: 'gray.50',
    borderColor: 'gray.800',
    _disabled: {
      borderColor: mode('gray.350', 'gray.950')(props),
    },
  },
})

const variantLink: SystemStyleFunction = (props) => ({
  color: mode('gray.900', 'white')(props),
})

const variants = {
  solid: variantSolid,
  secondary: variantSecondary,
  ghost: variantGhost,
  outline: variantOutline,
  pink: variantPink,
  link: variantLink,
}

export const Z4Button: ComponentStyleConfig = {
  baseStyle,
  sizes,
  defaultProps: {
    size: 'sm',
    variant: 'solid',
  },
  variants,
}
